import { useQuery, useMutation, useQueryClient, keepPreviousData } from "@tanstack/react-query";
import { Get } from "../config/api/get";
import { Post } from "../config/api/post";
import { useSelector } from "react-redux";
import { PARENTS, CLASSROOM, CONTENT_TYPE } from "../config/constants";
import swal from "../utils/swal";


export const useSearchStudents = (params = {}) => {
  const token = useSelector((state) => state.user.userToken);
  return useQuery({
    queryKey: ["search-students", params],
    queryFn: async () => {
      try {
        const { data } = await Get(PARENTS.searchStudents, token, params);
        return data.students || [];
      } catch (error) {
        console.error("Error fetching students:", error);
        throw error.data;
      }
    },
    enabled: !!token,
    placeholderData: keepPreviousData,
  });
};

const PARENTS_QUERY_KEY = ["get-parents"];

const invalidateParentsList = (queryClient) => {
  queryClient.invalidateQueries({ queryKey: PARENTS_QUERY_KEY });
};

export const useGetAllParents = (params = {}) => {
  const token = useSelector((state) => state.user.userToken);

  return useQuery({
    queryKey: [...PARENTS_QUERY_KEY, params],
    queryFn: async ({ signal }) => {
      try {
        const { data } = await Get(PARENTS.getAllParents, token, params, { signal });
        return data || { docs: [], totalPages: 1, totalDocs: 0 };
      } catch (error) {
        console.error("Error fetching parents:", error);
        throw error.data;
      }
    },
    enabled: !!token,
    placeholderData: keepPreviousData,
  });
};


export const useUpdateParentStatus = () => {
  const queryClient = useQueryClient();
  const token = useSelector((state) => state.user.userToken);

  return useMutation({
    mutationFn: async ({ parentId }) => {
      const body = await Get(PARENTS.toggleStatus + parentId, token);
      if (!body?.status) {
        throw new Error(body?.message || "Failed to update parent status");
      }
      return body;
    },
    onSuccess: (_data, variables) => {
      invalidateParentsList(queryClient);
      if (variables?.parentId) {
        queryClient.invalidateQueries({ queryKey: ["parent", variables.parentId] });
      }
    },
  });
};


export const useAddParent = () => {
  const queryClient = useQueryClient();
  const token = useSelector((state) => state.user.userToken);

  return useMutation({
    mutationFn: async ({ data }) => {
      try {
        const response = await Post(PARENTS.addParent, data, token, null, CONTENT_TYPE.FORM_DATA);

       if (response?.data?.status) {
          return response.data; // Success case
        } else {
          throw new Error(response?.data?.message || response?.response?.data?.message || "Failed to create parent");
        }
      } catch (error) {
        console.error("Error adding parent:", error);
         throw error.data;
      }
    },
    onSuccess: () => {
      invalidateParentsList(queryClient);
    },
  });
};


export const useGetParentAttendanceByMonth = (params) => {
  const token = useSelector((state) => state.user.userToken);

  return useQuery({
    queryKey: ["parent-attendance", params],
    queryFn: async () => {
      try {
        const { data } = await Get(PARENTS.getParentAttendanceByMonth + params.id, token, params);
        return data.attendance || []; // Default response structure
      } catch (error) {
        console.error("Error fetching parents:", error);
        throw error.data;
      }
    }
  });
}



export const useGetParentById = (parentId) => {
  const token = useSelector((state) => state.user.userToken);

  return useQuery({
    queryKey: ["parent", parentId],
    queryFn: async () => {
      try {
        const response = await Get(PARENTS.getParentById + parentId, token);
        return response.data.parent;
      } catch (error) {
        console.error("Error fetching parent:", error);
        throw error.data;
      }
    },
    enabled: !!token && !!parentId,
  });
}



export const useGetClassrooms = (values) => {
  const token = useSelector((state) => state.user.userToken);

  return useQuery({
    queryKey: "classrooms",
    queryFn: async () => {
      const response = await Get(`${CLASSROOM.getAllClassrooms}`, token);
      return response.data.docs;
    },
  })
};

export const useUpdateParent = () => {
  const queryClient = useQueryClient();
  const token = useSelector((state) => state.user.userToken);

  return useMutation({
    mutationFn: async ({ parentId, data }) => {
      console.log("dataLLLLL", data)
      try {
        const response = await Post(PARENTS.updateParent + parentId, data, token, null,CONTENT_TYPE.FORM_DATA);
        // console.log("dataLLLLL", response)
        if (response?.data?.status) {
          return response.data; // Success case
        } else {
          throw new Error(response?.data?.message || response?.response?.data?.message || "Failed to create parent");
        }
      } catch (error) {
        console.error("Error updating parent:", error);
        throw error.data;
      }
    },
    onSuccess: (_data, variables) => {
      invalidateParentsList(queryClient);
      if (variables?.parentId) {
        queryClient.invalidateQueries({ queryKey: ["parent", variables.parentId] });
      }
    },
  })
};


export const useDeleteParent = () => {
  const queryClient = useQueryClient();
  const token = useSelector((state) => state.user.userToken);

  return useMutation({
    mutationFn: async (parentId) => {
      const response = await Get(PARENTS.deleteParent + parentId, token);
      const body =
        typeof response?.status === "boolean"
          ? response
          : response?.response?.data || response?.data;
      if (!body?.status) {
        throw new Error(body?.message || "Failed to delete parent");
      }
      return body;
    },
    onSuccess: () => {
      invalidateParentsList(queryClient);
      queryClient.invalidateQueries({ queryKey: ["parent"] });
    },
  });
}


export const useMarkAttendance = () => {
  const queryClient = useQueryClient();
  const token = useSelector((state) => state.user.userToken);

  return useMutation({
    mutationFn: async ({ parentId, data }) => {
      try {
        const response = await Post(PARENTS.markAttendance + parentId, data, token);
        return response.data;
      } catch (error) {
        console.error("Error marking attendance:", error);
        throw error.data;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries(["parent-attendance"]);
    },
  });
}

export const useUpdateAttendance = () => {
  const queryClient = useQueryClient();
  const token = useSelector((state) => state.user.userToken);

  return useMutation({
    mutationFn: async ({ attendanceId, data }) => {
      try {
        const response = await Post(PARENTS.updateAttendance + attendanceId, data, token);
        console.log(">>>>>>", response)
        return response.data;
      } catch (error) {
        swal("Error", error.data.message, "error");
        throw error.data;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries(["parent"]);
      invalidateParentsList(queryClient);
      queryClient.invalidateQueries(["parent-attendance"]);
    },
  });
}

export const useDeleteAttendance = () => {
  const queryClient = useQueryClient();
  const token = useSelector((state) => state.user.userToken);

  return useMutation({
    mutationFn: async (attendanceId) => {
      try {
        const response = await Post(PARENTS.deleteAttendance + attendanceId,{}, token);
        return response.data;
      } catch (error) {
        console.error("Error deleting parent:", error);
        throw error.data;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries(["parent-attendance"]);
    },
  });
}